import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  InputLabel,
  OutlinedInput,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import * as Yup from 'yup';
import { Formik } from 'formik';

import MainCard from 'components/MainCard';
import PageHeader from 'components/PageHeader';
import { roleChipSx } from 'components/table/tableChipStyles';
import TableLoadedFooter from 'components/table/TableLoadedFooter';
import AnimateButton from 'components/@extended/AnimateButton';
import api from 'utils/api';
import { useToast } from 'contexts/ToastContext';

// ─── helpers ────────────────────────────────────────────────────────────────

function parseRoleListResponse(res) {
  const body = res?.data;
  const data = body?.data ?? body;

  const roles =
    data?.roles ??
    data?.role_list ??
    data?.roleList ??
    data?.list ??
    (Array.isArray(data) ? data : []);

  return Array.isArray(roles) ? roles : [];
}

function getRoleId(role) {
  return role?.role_id ?? role?.id;
}

function formatRoleLabel(roleName) {
  return roleName?.replace(/_/g, ' ') || '—';
}

function RoleChip({ label }) {
  return <Chip label={label} size="small" variant="outlined" sx={roleChipSx} />;
}

// ─── Add Role Dialog ─────────────────────────────────────────────────────────

function AddRoleDialog({ open, onClose, onAdded }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        elevation: 0,
        sx: { borderRadius: 3, border: '1px solid', borderColor: 'divider' },
      }}
    >
      <Formik
        initialValues={{ role_name: '', description: '' }}
        validationSchema={Yup.object().shape({
          role_name: Yup.string()
            .max(100, 'Role name must be at most 100 characters')
            .required('Role name is required'),
          description: Yup.string().max(255, 'Description must be at most 255 characters'),
        })}
        onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
          try {
            const payload = { role_name: values.role_name.trim() };
            if (values.description.trim()) payload.description = values.description.trim();

            const { data: body } = await api.post('/role/add', payload);

            if (!body.status) {
              setErrors({ submit: body.message || 'Failed to add role' });
              return;
            }

            resetForm();
            onAdded();   // refresh list
            onClose();
          } catch (err) {
            const msg = err.response?.data?.message || 'Failed to add role';
            const lower = msg.toLowerCase();
            if (lower.includes('name')) {
              setErrors({ role_name: msg });
            } else {
              setErrors({ submit: msg });
            }
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, submitCount, values }) => {
          const showErr = (field) => Boolean(errors[field] && (touched[field] || submitCount > 0));

          return (
            <form noValidate onSubmit={handleSubmit}>
              <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add New Role</DialogTitle>

              <DialogContent sx={{ pt: '12px !important' }}>
                <Stack spacing={2.5}>

                  {errors.submit && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {errors.submit}
                    </Alert>
                  )}

                  {/* Role Name */}
                  <Stack spacing={1}>
                    <InputLabel htmlFor="role-name">
                      Role Name <span style={{ color: 'red' }}>*</span>
                    </InputLabel>
                    <OutlinedInput
                      id="role-name"
                      name="role_name"
                      value={values.role_name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="e.g. BRANCH_MANAGER"
                      fullWidth
                      size="small"
                      error={showErr('role_name')}
                    />
                    {showErr('role_name') && (
                      <FormHelperText error>{errors.role_name}</FormHelperText>
                    )}
                  </Stack>

                  {/* Description */}
                  <Stack spacing={1}>
                    <InputLabel htmlFor="role-description">Description</InputLabel>
                    <OutlinedInput
                      id="role-description"
                      name="description"
                      value={values.description}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Short description (optional)"
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      error={showErr('description')}
                    />
                    {showErr('description') && (
                      <FormHelperText error>{errors.description}</FormHelperText>
                    )}
                  </Stack>

                </Stack>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  color="inherit"
                  disabled={isSubmitting}
                  sx={{ borderRadius: 2 }}
                >
                  Cancel
                </Button>
                <AnimateButton>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    sx={{ borderRadius: 2, minWidth: 110 }}
                  >
                    {isSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Add Role'}
                  </Button>
                </AnimateButton>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function RoleList() {
  const { showToast, showApiError } = useToast();
  const [roles, setRoles]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await api.get('/role/list');
      const ok  = res.data?.status !== false;
      if (ok) {
        setRoles(parseRoleListResponse(res));
      } else {
        setRoles([]);
        showToast(res.data?.message || 'Failed to load roles', { severity: 'error' });
      }
    } catch (err) {
      setRoles([]);
      showApiError(err, 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  return (
    <Box>
      <PageHeader
        title="Roles"
        subtitle="System roles and access levels."
        count={roles.length}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton onClick={fetchRoles} size="small" disabled={loading}>
                <ReloadOutlined />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<PlusOutlined />}
              onClick={() => setDialogOpen(true)}
              sx={{ borderRadius: 2, fontWeight: 600, px: 2 }}
            >
              Add Role
            </Button>
          </Stack>
        }
      />

      <MainCard contentSX={{ p: 0 }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            display: 'block',
            maxHeight: { xs: '55vh', md: 520 },
            overflowX: 'auto',
            overflowY: 'auto',
            borderBottom: 'none',
            '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
              borderBottom: 'none'
            }
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700, width: 48 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Role Name</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>Badge</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No roles found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role, idx) => (
                  <TableRow key={getRoleId(role)} hover>
                    <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>
                    <TableCell>
                      <Typography fontWeight={700} sx={{ letterSpacing: 0.5, fontSize: 13 }}>
                        {role.role_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <RoleChip label={formatRoleLabel(role.role_name)} />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {role.description || '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
              {!loading && roles.length > 0 ? (
                <TableLoadedFooter count={roles.length} singular="role" colSpan={4} />
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </MainCard>

      {/* Add Role Dialog */}
      <AddRoleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdded={fetchRoles}
      />
    </Box>
  );
}